import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Card, CardContent } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import api from '../../api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658'];

const AttendanceReportDashboard = () => {
  const formatLocal = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const today = new Date();
  let start, end;
  
  if (today.getDate() === 1) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    start = formatLocal(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    end = formatLocal(prevMonth);
  } else {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    start = formatLocal(new Date(today.getFullYear(), today.getMonth(), 1));
    end = formatLocal(yesterday);
  }

  const [filters, setFilters] = useState({ emp_id: '', start_date: start, end_date: end, department_id: '', designation_id: '', status: '' });
  const [dropdowns, setDropdowns] = useState({ departments: [], designations: [] });
  const [stats, setStats] = useState({ cards: {}, top_dlop: [], top_other_leaves: [], unallocated_dept: [], dept_emp_count: [], day_wise: [] });

  useEffect(() => {
    api.get('/correction-dashboard/filters-data').then(res => setDropdowns(res.data)).catch(console.error);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    const query = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v !== '')).toString();
    try {
      const res = await api.get(`/attendance-report/dashboard-stats?${query}`);
      setStats(res.data);
    } catch (err) { console.error("Failed to fetch dashboard stats", err); }
  };

  const getDisplayDateText = () => {
    if (filters.start_date && filters.end_date) {
      const s = new Date(filters.start_date);
      const e = new Date(filters.end_date);
      if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        return s.toLocaleString('default', { month: 'long', year: 'numeric' });
      }
      return `${filters.start_date} to ${filters.end_date}`;
    }
    return 'All Time';
  };

  const truncateText = (text, maxLength = 12) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* ROW 1: FILTERS */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
        <TextField size="small" label="Emp ID / Name" value={filters.emp_id} onChange={e => setFilters({...filters, emp_id: e.target.value})} sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
        <TextField select size="small" label="Department" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.departments.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.department_name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Designation" sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} value={filters.designation_id} onChange={e => setFilters({...filters, designation_id: e.target.value})}>
          <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All</MenuItem>
          {dropdowns.designations.map(d => <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.85rem' }}>{d.designation_name}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Status" value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} placeholder="e.g. DLOP" />
        <Button variant="contained" size="small" onClick={fetchStats} disableElevation sx={{ py: 1, bgcolor: '#1976d2' }}>Apply Filters</Button>
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pl: 0.5, fontWeight: 500 }}>
        Showing data for: <strong style={{ color: '#10394f' }}>{getDisplayDateText()}</strong> (Active Employees Only)
      </Typography>

      {/* ROW 2: COUNT CARDS (Updated to 5 layout items using 20% width) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1, mb: 1 }}>
        {[
          { label: 'Total Employees', value: stats.cards.total_employees || 0, color: '#1976d2' },
          { label: 'On-Duty Employees', value: stats.cards.on_duty_count || 0, color: '#9c27b0' },
          { label: 'Total DLOPs', value: stats.cards.dlop_count || 0, color: '#d32f2f' },
          { label: 'Total LOPs', value: stats.cards.lop_count || 0, color: '#ed6c02' },
          { label: 'Punctuality %', value: `${stats.cards.punctuality || 0}%`, color: '#2e7d32' }
        ].map((card, idx) => (
          <Box key={idx} sx={{ width: { xs: '100%', sm: '50%', md: '20%' }, p: 1 }}>
            <Card sx={{ borderTop: `4px solid ${card.color}`, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', borderRadius: 2 }} elevation={0}>
              <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{card.label}</Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* ROW 3: LEAVE CHARTS & UNALLOCATED PIE */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1, mb: 1 }}>
        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Top 10: DLOP</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_dlop} layout="vertical" margin={{ left: 0, right: 15, top: 5, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} fontSize={10} tick={{ fill: '#777' }} />
                  <YAxis dataKey="name" type="category" width={75} fontSize={10} tick={{ fill: '#333' }} tickFormatter={(tick) => truncateText(tick, 12)} />
                  <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#d32f2f" radius={[0, 3, 3, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
        
        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Top 10: Other Leaves</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_other_leaves} layout="vertical" margin={{ left: 0, right: 15, top: 5, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} fontSize={10} tick={{ fill: '#777' }} />
                  <YAxis dataKey="name" type="category" width={75} fontSize={10} tick={{ fill: '#333' }} tickFormatter={(tick) => truncateText(tick, 12)} />
                  <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#ffb300" radius={[0, 3, 3, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Unallocated by Department</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.unallocated_dept} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.unallocated_dept?.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ROW 4: Day-wise Graph & Dept Pie Chart */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
        <Box sx={{ width: { xs: '100%', md: '66.66%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Day-wise Employee Count (Non-0 TOH)</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.day_wise} margin={{ left: -10, right: 15, top: 5, bottom: 0 }}>
                  <XAxis dataKey="date_str" fontSize={10} tick={{ fill: '#777' }} />
                  <YAxis allowDecimals={false} fontSize={10} tick={{ fill: '#777' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 1 }}>
          <Paper sx={{ p: 1.5, height: 260, display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #eee' }} elevation={0}>
            <Typography variant="body2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#444' }}>Emp Count by Department</Typography>
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.dept_emp_count} dataKey="count" nameKey="department" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                    {stats.dept_emp_count?.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AttendanceReportDashboard;